import type { AxiosResponse } from 'axios';

/**
 * Téléchargement de fichiers reçus en blob, avec une extension qui correspond
 * au type RÉEL du contenu.
 *
 * Pourquoi : avec `<a download="nom">` sur une URL blob, c'est le nom choisi
 * ici qui est enregistré, pas celui du serveur. Sous Windows, l'application
 * d'ouverture est choisie sur la seule extension : un PNG enregistré en
 * `.pdf`, ou un fichier sans extension (« Carte grise »), ne s'ouvre pas.
 * macOS s'en sort via les métadonnées, d'où un bug « Windows only ».
 *
 * Règle : l'API fait foi sur l'extension (elle détecte le type réel dans le
 * contenu et l'annonce dans `Content-Disposition`) ; le front peut choisir la
 * partie lisible du nom, jamais contredire cette extension.
 */

/** Type MIME → extensions (la première est celle qu'on pose). */
const EXTENSIONS_BY_MIME: Record<string, string[]> = {
  'application/pdf': ['pdf'],
  'image/jpeg': ['jpg', 'jpeg', 'jpe'],
  'image/png': ['png'],
  'image/gif': ['gif'],
  'image/webp': ['webp'],
  'image/heic': ['heic'],
  'image/heif': ['heif'],
  'image/avif': ['avif'],
  'image/bmp': ['bmp'],
  'image/svg+xml': ['svg'],
  'image/tiff': ['tiff', 'tif'],
  'video/mp4': ['mp4'],
  'video/quicktime': ['mov'],
  'audio/mpeg': ['mp3'],
  'application/msword': ['doc'],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['docx'],
  'application/vnd.ms-excel': ['xls'],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['xlsx'],
  'application/vnd.oasis.opendocument.text': ['odt'],
  'application/vnd.oasis.opendocument.spreadsheet': ['ods'],
  'application/vnd.oasis.opendocument.presentation': ['odp'],
  'application/vnd.ms-powerpoint': ['ppt'],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['pptx'],
  'application/rtf': ['rtf'],
  'message/rfc822': ['eml'],
  'application/vnd.ms-outlook': ['msg'],
  'video/webm': ['webm'],
  'video/x-msvideo': ['avi'],
  'audio/mp4': ['m4a'],
  'audio/wav': ['wav'],
  'text/csv': ['csv'],
  'text/plain': ['txt'],
  'application/zip': ['zip'],
  'application/json': ['json'],
};

const MIME_BY_EXTENSION: Record<string, string> = Object.fromEntries(
  Object.entries(EXTENSIONS_BY_MIME).flatMap(([mime, exts]) => exts.map((ext) => [ext, mime])),
);

/** Suffixes qui ne sont jamais une extension de document. */
const NOT_AN_EXTENSION = new Set([
  // Artefacts d'anciennes versions de l'API (`libellé.${mime.extension(null)}`).
  'false', 'undefined', 'null', 'bin',
  // Exécutables : jamais posés ni tenus pour une extension (« garage-auto.com »).
  'exe', 'com', 'bat', 'cmd', 'msi', 'scr', 'pif', 'js', 'vbs', 'ps1', 'jar', 'sh', 'app', 'dmg', 'lnk', 'hta',
]);

function normalizeMimeType(mimeType?: string | null): string {
  return (mimeType || '').split(';')[0].trim().toLowerCase();
}

function getSuffix(name?: string | null): string | undefined {
  if (!name) return undefined;
  const dotIndex = name.lastIndexOf('.');
  if (dotIndex <= 0) return undefined;
  const ext = name.slice(dotIndex + 1).toLowerCase();
  return NOT_AN_EXTENSION.has(ext) ? undefined : ext;
}

/**
 * Extension d'un nom ANNONCÉ PAR LE SERVEUR, ou undefined : connue de la table,
 * ou 3-5 caractères alphanumériques contenant une lettre (`.dwg`…) — l'API
 * n'annonce que des extensions qu'elle a validées. Les suffixes de libellé
 * (« S.A.S », « 12.2024 ») et les exécutables n'en sont pas.
 */
export function getRealExtension(name?: string | null): string | undefined {
  const ext = getSuffix(name);
  if (!ext) return undefined;
  if (MIME_BY_EXTENSION[ext] || /^(?=.*[a-z])[a-z0-9]{3,5}$/.test(ext)) return ext;
  return undefined;
}

/**
 * Extension d'un nom CHOISI PAR L'ÉCRAN (libellé saisi) : seulement si elle est
 * connue de la table, ou identique à celle qu'on veut poser. « Garage.auto »
 * n'a pas d'extension (même règle que l'API, qui exige une extension connue).
 */
function getDisplayNameExtension(name: string, targetExt: string): string | undefined {
  const ext = getSuffix(name);
  if (!ext) return undefined;
  return MIME_BY_EXTENSION[ext] || ext === targetExt ? ext : undefined;
}

function sanitizeFileName(fileName: string): string {
  return fileName.replace(/[\\/:*?"<>|]+/g, '-').replace(/[.\s]+$/, '').trim() || 'document';
}

function sameFileType(extA: string, extB: string): boolean {
  return extA === extB || (!!MIME_BY_EXTENSION[extA] && MIME_BY_EXTENSION[extA] === MIME_BY_EXTENSION[extB]);
}

/** Remplace (ou ajoute) l'extension de `fileName` par `extension`, sauf si elle est équivalente. */
export function withExtension(fileName: string, extension: string): string {
  const name = sanitizeFileName(fileName);
  const targetExt = extension.toLowerCase();
  const currentExt = getDisplayNameExtension(name, targetExt);
  if (currentExt && sameFileType(currentExt, targetExt)) return name;
  const stem = currentExt ? name.slice(0, -(currentExt.length + 1)) : name;
  return `${stem}.${extension}`;
}

/** Aligne l'extension de `fileName` sur `mimeType` ; type inconnu ou générique → nom inchangé. */
export function withExtensionForMimeType(fileName: string, mimeType?: string | null): string {
  const targetExt = EXTENSIONS_BY_MIME[normalizeMimeType(mimeType)]?.[0];
  return targetExt ? withExtension(fileName, targetExt) : sanitizeFileName(fileName);
}

/**
 * Nom de fichier annoncé par le serveur (`Content-Disposition`), ou null.
 * Gère `filename*` (RFC 5987, langue optionnelle) et la quoted-string de
 * `filename` (`;` et `\"` inclus). Nécessite que l'API expose le header en
 * CORS (`exposedHeaders`, api/src/main.ts).
 */
export function getFileNameFromHeaders(headers: Record<string, any> | undefined): string | null {
  const disposition: string = headers?.['content-disposition'] || '';
  const extended = disposition.match(/filename\*\s*=\s*([\w-]+)'[^']*'([^;\s]+)/i);
  if (extended) {
    try {
      return decodeURIComponent(extended[2]);
    } catch {
      // encodage invalide : on tente le paramètre filename simple
    }
  }
  const plain = disposition.match(/filename\s*=\s*(?:"((?:\\.|[^"\\])*)"|([^;\s]+))/i);
  const value = plain?.[1] !== undefined ? plain[1].replace(/\\(.)/g, '$1') : plain?.[2];
  return value?.trim() || null;
}

/** Déclenche l'enregistrement d'un blob sous `fileName`. */
export function triggerBlobDownload(blob: Blob, fileName: string): void {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  setTimeout(() => {
    link.remove();
    window.URL.revokeObjectURL(url);
  }, 100);
}

/** Type MIME d'une réponse blob : header `Content-Type`, sinon type du blob. */
export function getResponseMimeType(response: AxiosResponse<Blob>): string {
  return normalizeMimeType(response.headers?.['content-type']) || normalizeMimeType(response.data?.type);
}

/** Nom final d'un fichier reçu : partie lisible choisie par l'écran, extension du serveur. */
export function resolveDownloadFileName(
  serverName: string | null,
  mimeType: string,
  preferredName?: string,
): string {
  const serverExt = getRealExtension(serverName);
  if (serverName && serverExt) {
    return preferredName ? withExtension(preferredName, serverExt) : sanitizeFileName(serverName);
  }
  // Pas d'extension côté serveur (ancienne API, type inconnu) : on se rabat sur le Content-Type.
  return withExtensionForMimeType(preferredName || serverName || 'document', mimeType);
}

/**
 * Enregistre une réponse axios `responseType: 'blob'`.
 * `preferredName` = partie lisible du nom souhaitée par l'écran (avec ou sans
 * extension) ; l'extension finale est toujours celle du serveur, à défaut
 * celle du type reçu.
 */
export function downloadBlobResponse(response: AxiosResponse<Blob>, preferredName?: string): void {
  const serverName = getFileNameFromHeaders(response.headers);
  const mimeType = getResponseMimeType(response);
  const blob = new Blob([response.data], { type: mimeType || 'application/octet-stream' });
  triggerBlobDownload(blob, resolveDownloadFileName(serverName, mimeType, preferredName));
}
